// ** React Imports
import React, { Dispatch, SetStateAction } from 'react'

// ** MUI Imports
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Tooltip } from '@mui/material'
import { useTheme } from '@emotion/react'
import { X } from 'react-feather'
import { LoadingButton } from '@mui/lab'
import { useUser } from 'src/lib/contexts/UserContext'

interface FloGenericDialogProps {
  id?: string
  isOpen: boolean
  handleClose: Dispatch<SetStateAction<boolean>>
  handleJob?: (() => void) | (() => Promise<void>)
  jobButtonText?: string
  closeButtonText?: string
  content: JSX.Element
  referrer?: string
  title: React.ReactNode
  subTitle?: string
  isLoading?: boolean
  endIcon?: JSX.Element
  width?: number
  maxWidth?: number
  primaryBtnEnabled?: boolean
  hideTitleBar?: boolean
  closeButtonVariant?: 'text' | 'outlined' | 'contained'
  hideCloseIcon?: boolean
  contentCss?: any
  primaryBtnCss?: {}
  actionBtn?: JSX.Element
  footerTxt?: React.ReactNode
  jobButtonDisabled?: boolean
  jobButtonTooltip?: string
  extraActionButtonText?: string
  handleExtraActionButton?: () => Promise<void>
  extraActionButtonCSS?: {}
  extraActionButtonDisable?: boolean
  hideTopBottomBorder?: boolean
  shouldDisableOutsideClick?: boolean
  jobBtnVariant?: 'text' | 'outlined' | 'contained'
  shouldCloseBtnBePrimary?: boolean
}

const FloGenericDialog: React.FC<FloGenericDialogProps> = ({
  id,
  isOpen,
  handleClose,
  handleJob,
  jobButtonText,
  closeButtonText,
  content,
  referrer = '',
  title,
  isLoading,
  endIcon,
  width,
  maxWidth,
  primaryBtnEnabled = true,
  closeButtonVariant = 'text',
  hideCloseIcon = false,
  contentCss = { padding: '1rem 1rem' },
  primaryBtnCss,
  actionBtn,
  subTitle,
  footerTxt,
  jobButtonDisabled = false,
  jobButtonTooltip,
  hideTitleBar = false,
  extraActionButtonText,
  handleExtraActionButton,
  extraActionButtonCSS,
  extraActionButtonDisable,
  hideTopBottomBorder = false,
  shouldDisableOutsideClick = false,
  shouldCloseBtnBePrimary = false,
  jobBtnVariant = 'contained'
}) => {
  const { user } = useUser()
  const executeJob = () => {
    if (typeof handleJob === 'undefined') return
    handleJob()
  }
  const theme: any = useTheme()
  return (
    <Dialog
      open={isOpen}
      onClose={() => {
        if (!shouldDisableOutsideClick) handleClose(false)
      }}
      PaperProps={{
        id: id,
        sx: {
          borderRadius: 4,
          gap: 4,
          minWidth: '440px',
          maxWidth: Boolean(maxWidth) ? `${maxWidth}px` : '600px',
          width: `${width}px`,
          border: '1px solid #E0E0E0',
          overflow: 'visible',
          position: 'relative'
        }
      }}
    >
      {!hideTitleBar ? (
        <DialogTitle
          variant='body1'
          sx={{
            padding: '1rem 1rem 1rem 1rem',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            fontWeight: 600,
            fontSize: '16px',
            color: `${theme.palette.text.header}`,
            borderBottom: hideTopBottomBorder ? '' : '1px solid #F0F0F0'
          }}
        >
          <div className='flex flex-row items-start justify-between w-full'>
            {title}
            {!Boolean(hideCloseIcon) ? (
              <X
                style={{ color: '#6E6E6E', strokeWidth: '3px', opacity: '100%' }}
                size={20}
                className='cursor-pointer'
                onClick={() => {
                  handleClose(false)
                }}
              />
            ) : (
              <></>
            )}
          </div>
          <p className='text-xs text-[#949494] font-normal'> {subTitle}</p>
        </DialogTitle>
      ) : (
        <></>
      )}
      <DialogContent id={id} sx={contentCss}>
        {content}
      </DialogContent>
      {primaryBtnEnabled ? (
        <DialogActions
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            padding: '1rem !important',
            width: '100%',
            borderTop: hideTopBottomBorder ? '' : '1px solid #F0F0F0'
          }}
        >
          <p className='text-sm text-left text-gray-dark'>{footerTxt}</p>
          <div className='absolute left-4'>{actionBtn ? actionBtn : <></>}</div>
          <div className='flex flex-row gap-2'>
            {closeButtonText ? (
              <Button
                onClick={() => handleClose(false)}
                variant={closeButtonVariant}
                size='small'
                color={shouldCloseBtnBePrimary ? 'primary' : 'secondary'}
                sx={{ maxWidth: 100 }}
              >
                {closeButtonText}
              </Button>
            ) : (
              <></>
            )}
            {Boolean(extraActionButtonText) ? (
              <LoadingButton
                id={`${referrer}${referrer != '' ? '-' : ''}extra-action-button`}
                onClick={handleExtraActionButton}
                size='small'
                loadingPosition='end'
                loading={isLoading}
                sx={{
                  fontSize: 14,
                  width: 'fit-content',
                  minWidth: '100px',
                  ...extraActionButtonCSS,
                  outline: 'none',
                  border: '1px solid #E0E0E0'
                }}
                disabled={user?.isDemoUser || extraActionButtonDisable}
                className='disabled:text-gray-dark disabled:bg-coal-light disabled:border-none'
              >
                {extraActionButtonText}
              </LoadingButton>
            ) : (
              <></>
            )}
            {jobButtonText ? (
              <Tooltip title={jobButtonDisabled && jobButtonTooltip ? jobButtonTooltip : ''} arrow>
                <span>
                  <LoadingButton
                    id={`${referrer}${referrer != '' ? '-' : ''}job-button`}
                    onClick={executeJob}
                    size='small'
                    loadingPosition='end'
                    loading={isLoading}
                    endIcon={Boolean(endIcon) ? endIcon : <></>}
                    variant={jobBtnVariant}
                    color={'primary'}
                    sx={{ fontSize: 14, width: 'fit-content', minWidth: '100px', ...primaryBtnCss, outline: 'none' }}
                    disabled={user?.isDemoUser || jobButtonDisabled}
                    className={
                      shouldCloseBtnBePrimary ? '' : 'disabled:text-gray-dark disabled:bg-coal-light disabled:border-none'
                    }
                  >
                    {jobButtonText}
                  </LoadingButton>
                </span>
              </Tooltip>
            ) : (
              <></>
            )}
          </div>
        </DialogActions>
      ) : (
        <></>
      )}
    </Dialog>
  )
}

export default FloGenericDialog
